import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check,
  Package,
  Truck,
  MapPin,
  ShoppingBag,
  Sparkles,
  Sun,
  Moon,
  Coffee,
  MessageCircle,
  Headphones,
  ArrowRight,
  Loader2,
  FlaskConical,
} from "lucide-react";
import magHero from "@/assets/product-hero/mag-absorb-hero.png.asset.json";
import liverHero from "@/assets/product-hero/liver-biome-hero.png.asset.json";
import ovaHero from "@/assets/product-hero/ova-balance-hero.png.asset.json";
import { InlineConsentCard } from "@/components/consent/InlineConsentCard";
import { InstallAppCard } from "@/components/notifications/InstallAppCard";
import { useT } from "@/lib/i18n/provider";
import { confirmationStageIndex, formatOrderETA } from "@/lib/order-success";
import { whatsappSupportUrl } from "@/lib/contact-info";

interface OrderItem {
  title: string;
  variantTitle: string | null;
  sku: string | null;
  quantity: number;
  unitPaise: number;
  linePaise: number;
  handle: string | null;
}
interface OrderInfo {
  name: string;
  status: string;
  financialStatus: string | null;
  fulfillmentStatus: string | null;
  currency: string;
  subtotalPaise: number;
  totalPaise: number;
  email: string | null;
  placedAt: string | null;
  promisedDispatchDate?: string | null;
  customerName: string | null;
  city: string | null;
  items: OrderItem[];
  shippingAddress?: Record<string, any> | null;
  paid?: boolean;
  metaEventId?: string | null;
}

const rupees = (paise: number) =>
  `₹${(paise / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const PRODUCT_META: Record<
  string,
  {
    image: string;
    accent: string;
    routine: { time: "morning" | "afternoon" | "night"; when: string };
    insight: { headline: string; body: string; cta: string; href: string };
    duration: (qty: number) => string;
  }
> = {
  "mag-absorb-pro": {
    image: magHero.url,
    accent: "from-[#1E5EFF]/15 to-[#1E5EFF]/0",
    routine: { time: "night", when: "30 min before bed" },
    insight: {
      headline: "5 Bioavailable Forms of Magnesium",
      body: "Mag Absorb Pro combines liposomal bisglycinate, Aquamin®, chloride, aspartate & gluconate, staged for absorption all night.",
      cta: "Learn why it matters",
      href: "/products/mag-absorb-pro",
    },
    duration: (q) => `${q * 30}-day protocol`,
  },
  "liver-biome-plus": {
    image: liverHero.url,
    accent: "from-[#1E5EFF]/15 to-[#1E5EFF]/0",
    routine: { time: "morning", when: "After breakfast" },
    insight: {
      headline: "Liposomal Curcumin + Silymarin",
      body: "Liver Biome Plus delivers actives in phospholipid carriers for up to 29× the absorption of standard turmeric.",
      cta: "Read the science",
      href: "/products/liver-biome-plus",
    },
    duration: (q) => `${q * 30}-day protocol`,
  },
  "ova-balance": {
    image: ovaHero.url,
    accent: "from-[#1E5EFF]/15 to-[#1E5EFF]/0",
    routine: { time: "afternoon", when: "With lunch" },
    insight: {
      headline: "40:1 Myo:D-Chiro Inositol Ratio",
      body: "The research-backed ovarian ratio, paired with Berberine HCl and Quatrefolic® L-5-MTHF for cycle support.",
      cta: "See the protocol",
      href: "/products/ova-balance",
    },
    duration: (q) => `${q * 30}-day protocol`,
  },
};

const STAGES = [
  { key: "confirmed", labelKey: "ordc.stage.confirmed", Icon: Check },
  { key: "packing", labelKey: "ordc.stage.packing", Icon: Package },
  { key: "shipped", labelKey: "ordc.stage.shipped", Icon: Truck },
  { key: "out", labelKey: "ordc.stage.out", Icon: MapPin },
  { key: "delivered", labelKey: "ordc.stage.delivered", Icon: Sparkles },
] as const;

function getProductMeta(handle: string | null) {
  if (!handle) return null;
  return PRODUCT_META[handle] ?? null;
}

export const Route = createFileRoute("/order-success")({
  head: () => ({
    meta: [
      { title: "Order confirmed. TruWe" },
      { name: "description", content: "Your TruWe order is confirmed. Track it and ask us about your formula on WhatsApp." },
      { property: "og:title", content: "Order confirmed. TruWe" },
      { property: "og:description", content: "Track your TruWe order and ask us about your formula on WhatsApp." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: OrderSuccessPage,
});

function OrderSuccessPage() {
  const t = useT();
  const [order, setOrder] = useState<OrderInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orderName, setOrderName] = useState<string | null>(null);
  const [paymentId, setPaymentId] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const name = params.get("order");
    // Legacy links may still carry `pid` — never re-persist it; strip it
    // from history immediately so it doesn't linger in browser history/
    // referrers. Access is now gated by the HttpOnly confirmation cookie.
    const legacyPid = params.get("pid") || params.get("payment_id");
    setOrderName(name);
    if (legacyPid) {
      const clean = new URLSearchParams(params);
      clean.delete("pid");
      clean.delete("payment_id");
      const qs = clean.toString();
      window.history.replaceState(null, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
    }

    if (!name) {
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        let authHeader: Record<string, string> = {};
        try {
          const { supabase } = await import("@/integrations/supabase/client");
          const { data } = await supabase.auth.getSession();
          const token = data.session?.access_token;
          if (token) authHeader = { authorization: `Bearer ${token}` };
        } catch {
          /* anonymous is fine when the confirmation cookie is present */
        }
        const res = await fetch("/api/public/orders/lookup", {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json", ...authHeader },
          body: JSON.stringify({ order: name }),
        });
        const data = (await res.json()) as { success?: boolean; order?: OrderInfo; error?: string };
        if (cancelled) return;
        if (!res.ok || !data.success || !data.order) {
          setError(data.error || t("ordc.loadError"));
        } else {
          setOrder(data.order);
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.message || t("ordc.loadError"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Close the anonymous cart-recovery intent for this visitor. The cart is
  // cleared and the tab navigates here in the same tick, so the debounced
  // empty-cart sync never lands — without this a shopper who just paid could
  // still receive a "you left something behind" push.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { getAnonymousId } = await import("@/lib/identity/anonymousId");
        const anonymousId = getAnonymousId();
        if (!anonymousId || cancelled) return;
        const { markVisitorCartConverted } = await import("@/lib/cart-tracking.functions");
        await markVisitorCartConverted({ data: { anonymousId } });
        // Also close the saved per-device cart so it is never restored.
        const { getDeviceIdWhenIdle } = await import("@/lib/identity/deviceFingerprint");
        const deviceId = await getDeviceIdWhenIdle();
        if (deviceId && !cancelled) {
          const { markDeviceCartConverted } = await import("@/lib/device-identity.functions");
          await markDeviceCartConverted({ data: { deviceId } });
        }
      } catch {
        /* never blocks the confirmation screen */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);


  // Fire GA4 + Meta Purchase via the shared ecommerce layer ONLY when the
  // server confirms payment AND has issued a meta_event_id (server-issued
  // dedup id — the same id the CAPI Purchase uses). Never fires on pending,
  // failed, cancelled or unverified payments. Persistent per-transaction
  // guard in the shared layer defeats refresh / back-forward navigation.
  useEffect(() => {
    if (!order) return;
    if (!order.paid || !order.metaEventId) return;
    const orderId = order.name || orderName;
    if (!orderId) return;
    const items = order.items
      .filter((it) => it.handle)
      .map((it) => ({
        id: it.handle as string,
        name: it.title || (it.handle as string),
        variant: it.variantTitle ?? undefined,
        price: it.unitPaise / 100,
        quantity: it.quantity,
      }));
    if (!items.length) return;
    void import("@/lib/analytics/ecommerce").then(({ trackPurchase }) => {
      trackPurchase({
        transactionId: orderId,
        metaEventId: order.metaEventId as string,
        value: order.totalPaise / 100,
        items,
      });
    });
    // Enhanced Conversions — hashed customer identifiers, consent-gated,
    // sent only alongside the verified purchase.
    if (order.email) {
      void import("@/lib/analytics/enhanced-conversions").then(
        ({ sendPurchaseWithEnhancedConversions }) => {
          sendPurchaseWithEnhancedConversions({
            transactionId: orderId,
            valueInr: order.totalPaise / 100,
            email: order.email,
          });
        },
      );
    }
  }, [order, orderName]);

  const activeStage = useMemo(
    () => (order ? confirmationStageIndex(order.fulfillmentStatus, order.status) : 0),
    [order],
  );

  const eta = useMemo(
    () => formatOrderETA(order?.placedAt || null, order?.promisedDispatchDate || null),
    [order],
  );
  const firstName = order?.customerName?.split(" ")[0] || "";
  const ingredientProduct = order?.items.find((item) => getProductMeta(item.handle))?.title ?? null;
  const ingredientChatUrl = whatsappSupportUrl(
    ingredientProduct
      ? `Hi TruWe! Please tell me about the ingredients in ${ingredientProduct}.`
      : "Hi TruWe! Please tell me about the ingredients in my supplement.",
  );

  const routine = useMemo(() => {
    const slots: Record<"morning" | "afternoon" | "night", OrderItem[]> = {
      morning: [],
      afternoon: [],
      night: [],
    };
    (order?.items || []).forEach((it) => {
      const meta = getProductMeta(it.handle);
      if (meta) slots[meta.routine.time].push(it);
    });
    return slots;
  }, [order]);

  const shipping = order?.shippingAddress;
  const shippingLine = shipping
    ? [
        [shipping.address1, shipping.address2].filter(Boolean).join(", "),
        [shipping.city, shipping.zip].filter(Boolean).join(" "),
        shipping.province,
      ]
        .filter(Boolean)
        .join(" · ")
    : order?.city || null;

  return (
    <main className="min-h-screen bg-gradient-to-b from-white via-[#F7F7F5] to-[#F7F7F5] pb-32 text-[#2C2C2C]">
      {/* HERO */}
      <section className="relative overflow-hidden px-5 pt-10 sm:pt-16">
        {/* Confetti */}
        <ConfettiBurst />
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto flex max-w-2xl flex-col items-center text-center"
        >
          <AnimatedCheck />
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="mt-6 font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl"
          >
            {firstName ? t("ordc.thankYouNamed", { name: firstName }) : t("ordc.thankYou")} 🎉
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="mt-2 max-w-md text-[15px] text-[#2C2C2C]/65 sm:text-base"
          >
            {t("ordc.confirmed")}
          </motion.p>
        </motion.div>

        {/* Summary card */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.6 }}
          className="mx-auto mt-8 grid w-full max-w-2xl grid-cols-1 gap-3 rounded-[28px] bg-white p-5 shadow-[0_8px_40px_rgba(20,30,60,0.06)] ring-1 ring-black/[0.04] sm:grid-cols-3 sm:p-6"
        >
          <SummaryStat label={t("ordc.stat.order")} value={order?.name || orderName || "-"} mono />
          <SummaryStat label={t("ordc.stat.eta")} value={eta} />
          <SummaryStat
            label={t("ordc.stat.shippingTo")}
            value={shippingLine || (loading ? t("ordc.loadingShort") : "-")}
            truncate
          />
        </motion.div>

        {loading && (
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-[#2C2C2C]/60">
            <Loader2 className="h-4 w-4 animate-spin" /> {t("ordc.loading")}
          </div>
        )}
        {!loading && error && (
          <p className="mx-auto mt-6 max-w-md rounded-2xl bg-white p-4 text-center text-sm text-[#2C2C2C]/70 ring-1 ring-black/5">
            {error}
          </p>
        )}
        <div className="mx-auto mt-8 max-w-md">
          <InlineConsentCard source="order_success" variant="compact" />
        </div>
      </section>

      {/* PROGRESS TRACKER */}
      {order && (
        <Section delay={0.05}>
          <SectionHeader eyebrow={t("ordc.progress.eyebrow")} title={t("ordc.progress.title")} />
          <div className="rounded-[28px] bg-white p-5 shadow-[0_6px_30px_rgba(20,30,60,0.05)] ring-1 ring-black/[0.04] sm:p-7">
            <ProgressTracker active={activeStage} t={t} />
          </div>
        </Section>
      )}

      {/* ORDERED PRODUCTS */}
      {order && order.items.length > 0 && (
        <Section delay={0.1}>
          <SectionHeader eyebrow={t("ordc.box.eyebrow")} title={t("ordc.box.title")} />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {order.items.map((it, idx) => (
              <ProductCard key={idx} item={it} eta={eta} index={idx} t={t} />
            ))}
          </div>
        </Section>
      )}

      {/* INGREDIENT HELP */}
      <Section delay={0.05}>
        <div className="overflow-hidden rounded-[28px] bg-white p-6 shadow-[0_10px_40px_rgba(20,30,60,0.07)] ring-1 ring-black/[0.04] sm:p-8">
          <div className="flex items-start gap-4 sm:items-center">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#1E5EFF]/10 text-[#1E5EFF]">
              <FlaskConical className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-display text-xl font-bold sm:text-2xl">{t("ordc.ingredients.title")}</h3>
              <p className="mt-1 text-sm text-[#2C2C2C]/65 sm:text-[15px]">{t("ordc.ingredients.sub")}</p>
            </div>
          </div>
          <a
            href={ingredientChatUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1E5EFF] px-5 py-3 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(30,94,255,0.24)] transition active:scale-[0.98] hover:bg-[#1E5EFF]/90 sm:w-auto"
          >
            <MessageCircle className="h-4 w-4" /> {t("ordc.ingredients.cta")}
          </a>
        </div>
      </Section>

      {/* DAILY ROUTINE */}
      {order && (routine.morning.length || routine.afternoon.length || routine.night.length) ? (
        <Section delay={0.05}>
          <SectionHeader eyebrow={t("ordc.routine.eyebrow")} title={t("ordc.routine.title")} />
          <div className="overflow-hidden rounded-[28px] bg-white shadow-[0_6px_30px_rgba(20,30,60,0.05)] ring-1 ring-black/[0.04]">
            <RoutineRow time={t("ordc.routine.morning")} Icon={Sun} items={routine.morning} restLabel={t("ordc.routine.restDay")} />
            <RoutineRow time={t("ordc.routine.afternoon")} Icon={Coffee} items={routine.afternoon} restLabel={t("ordc.routine.restDay")} />
            <RoutineRow time={t("ordc.routine.night")} Icon={Moon} items={routine.night} restLabel={t("ordc.routine.restDay")} last />
          </div>
        </Section>
      ) : null}

      {/* SCIENCE INSIGHT */}
      {order && order.items.length > 0 && (
        <Section delay={0.05}>
          <ScienceInsightCard items={order.items} eyebrow={t("ordc.insight.eyebrow")} />
        </Section>
      )}

      {/* NEED HELP */}
      <Section delay={0.05}>
        <SectionHeader eyebrow={t("ordc.help.eyebrow")} title={t("ordc.help.title")} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <HelpCard
            icon={MessageCircle}
            title={t("ordc.help.ingredients")}
            sub={t("ordc.help.ingredientsSub")}
            href={ingredientChatUrl}
          />
          <HelpCard
            icon={Package}
            title={t("ordc.help.track")}
            sub={t("ordc.help.trackSub")}
            to="/track-order"
            search={{ order: orderName, pid: paymentId }}
          />
          <HelpCard
            icon={Headphones}
            title={t("ordc.help.support")}
            sub={t("ordc.help.supportSub")}
            to="/contact"
          />
        </div>
      </Section>

      {/* INSTALL APP */}
      <Section delay={0.05}>
        <InstallAppCard />
      </Section>


      {/* STICKY FOOTER CTA */}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 px-4 pb-4 pt-3 sm:pb-6">
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, type: "spring", stiffness: 220, damping: 28 }}
          className="pointer-events-auto mx-auto flex w-full max-w-2xl items-center gap-2 rounded-full bg-white/85 p-2 shadow-[0_12px_40px_rgba(20,30,60,0.12)] ring-1 ring-black/5 backdrop-blur-xl"
        >
          <Link
            to={"/track-order" as any}
            search={{ order: orderName, pid: paymentId } as any}
            className="flex-1 rounded-full bg-[#1E5EFF] px-4 py-3 text-center text-sm font-semibold text-white transition active:scale-[0.98] hover:bg-[#1E5EFF]/90"
          >
            {t("ordc.cta.track")}
          </Link>
          <Link
            to="/ingredients"
            className="flex-1 rounded-full px-4 py-3 text-center text-sm font-semibold text-[#2C2C2C] transition hover:bg-[#F7F7F5]"
          >
            {t("ordc.cta.science")}
          </Link>
        </motion.div>
      </div>
    </main>
  );
}

/* ---------- Sub-components ---------- */

function Section({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className="mx-auto mt-10 w-full max-w-2xl px-5 sm:mt-14"
    >
      {children}
    </motion.section>
  );
}

function SectionHeader({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <header className="mb-4">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#1E5EFF]/80">
        {eyebrow}
      </p>
      <h2 className="mt-1 font-display text-xl font-bold text-[#2C2C2C] sm:text-2xl">{title}</h2>
    </header>
  );
}

function SummaryStat({
  label,
  value,
  mono,
  truncate,
}: {
  label: string;
  value: string;
  mono?: boolean;
  truncate?: boolean;
}) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-[#2C2C2C]/45">
        {label}
      </p>
      <p
        className={`mt-1 text-[15px] font-semibold text-[#2C2C2C] ${mono ? "font-mono" : ""} ${
          truncate ? "truncate" : ""
        }`}
        title={value}
      >
        {value}
      </p>
    </div>
  );
}

function AnimatedCheck() {
  return (
    <div className="relative">
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 220, damping: 18 }}
        className="relative flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-[#1E5EFF] to-[#1448cc] shadow-[0_18px_50px_rgba(30,94,255,0.4)] sm:h-28 sm:w-28"
      >
        <motion.span
          initial={{ scale: 1, opacity: 0.6 }}
          animate={{ scale: 1.6, opacity: 0 }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }}
          className="absolute inset-0 rounded-full bg-[#1E5EFF]/40"
        />
        <svg viewBox="0 0 52 52" className="relative h-12 w-12 sm:h-14 sm:w-14" fill="none">
          <motion.path
            d="M14 27 L23 36 L40 18"
            stroke="white"
            strokeWidth={4.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ delay: 0.25, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          />
        </svg>
      </motion.div>
    </div>
  );
}

function ConfettiBurst() {
  const pieces = useMemo(
    () =>
      Array.from({ length: 18 }).map((_, i) => ({
        id: i,
        x: Math.random() * 100,
        delay: Math.random() * 0.6,
        rot: Math.random() * 360,
        color: ["#1E5EFF", "#7AA9FF", "#2C2C2C", "#D9D9D6"][i % 4],
      })),
    [],
  );
  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 z-0 h-64 overflow-hidden">
      {pieces.map((p) => (
        <motion.span
          key={p.id}
          initial={{ y: -40, x: `${p.x}vw`, rotate: 0, opacity: 0 }}
          animate={{ y: 280, rotate: p.rot, opacity: [0, 1, 1, 0] }}
          transition={{ duration: 2.4, delay: p.delay, ease: "easeOut" }}
          style={{ background: p.color }}
          className="absolute h-2 w-2 rounded-[2px]"
        />
      ))}
    </div>
  );
}

function ProgressTracker({ active, t }: { active: number; t: (k: any, v?: any) => string }) {
  return (
    <ol className="relative">
      {/* Desktop horizontal */}
      <div className="hidden sm:block">
        <div className="relative px-5">
          <div className="relative h-1 rounded-full bg-[#D9D9D6]">
            <motion.div
              initial={{ width: 0 }}
              animate={{
                width:
                  STAGES.length > 1
                    ? `${(active / (STAGES.length - 1)) * 100}%`
                    : "0%",
              }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-y-0 left-0 rounded-full bg-[#1E5EFF]"
            />
          </div>
        </div>
        <div className="mt-[-22px] flex items-start justify-between">
          {STAGES.map((s, i) => {
            const done = i <= active;
            return (
              <li
                key={s.key}
                className="flex flex-1 flex-col items-center text-center"
              >
                <motion.div
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.15 + i * 0.1, type: "spring", stiffness: 220 }}
                  className={`flex h-10 w-10 items-center justify-center rounded-full ring-4 ring-white ${
                    done
                      ? "bg-[#1E5EFF] text-white shadow-[0_6px_18px_rgba(30,94,255,0.35)]"
                      : "bg-white text-[#2C2C2C]/40 ring-[#D9D9D6]"
                  }`}
                  style={done ? {} : { boxShadow: "inset 0 0 0 1.5px #D9D9D6" }}
                >
                  <s.Icon className="h-4 w-4" strokeWidth={2.4} />
                </motion.div>
                <p
                  className={`mt-2 min-h-[2.25rem] whitespace-nowrap text-[11px] font-medium leading-tight ${
                    done ? "text-[#2C2C2C]" : "text-[#2C2C2C]/45"
                  }`}
                >
                  {t(s.labelKey)}
                </p>
              </li>
            );
          })}
        </div>
      </div>

      {/* Mobile vertical */}
      <div className="sm:hidden">
        {STAGES.map((s, i) => {
          const done = i <= active;
          return (
            <li key={s.key} className="relative flex items-start gap-3 pb-4 last:pb-0">
              {i < STAGES.length - 1 && (
                <span
                  className={`absolute left-[19px] top-10 h-[calc(100%-1.5rem)] w-0.5 ${
                    i < active ? "bg-[#1E5EFF]" : "bg-[#D9D9D6]"
                  }`}
                />
              )}
              <div
                className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                  done
                    ? "bg-[#1E5EFF] text-white shadow-[0_6px_18px_rgba(30,94,255,0.3)]"
                    : "bg-white text-[#2C2C2C]/40"
                }`}
                style={done ? {} : { boxShadow: "inset 0 0 0 1.5px #D9D9D6" }}
              >
                <s.Icon className="h-4 w-4" strokeWidth={2.4} />
              </div>
              <div className="pt-2">
                <p
                  className={`text-sm font-semibold ${
                    done ? "text-[#2C2C2C]" : "text-[#2C2C2C]/45"
                  }`}
                >
                  {t(s.labelKey)}
                </p>
              </div>
            </li>
          );
        })}
      </div>
    </ol>
  );
}

function ProductCard({
  item,
  eta,
  index,
  t,
}: {
  item: OrderItem;
  eta: string;
  index: number;
  t: (k: any, v?: any) => string;
}) {
  const meta = getProductMeta(item.handle);
  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.1 + index * 0.08, duration: 0.5 }}
      whileHover={{ y: -3 }}
      className="group relative overflow-hidden rounded-[24px] bg-white p-5 shadow-[0_6px_30px_rgba(20,30,60,0.05)] ring-1 ring-black/[0.04] transition-shadow hover:shadow-[0_14px_40px_rgba(20,30,60,0.08)]"
    >
      {meta && (
        <div
          className={`absolute inset-x-0 top-0 h-32 bg-gradient-to-b ${meta.accent}`}
          aria-hidden
        />
      )}
      <div className="relative flex items-start gap-4">
        <div className="relative h-24 w-20 shrink-0 sm:h-28 sm:w-24">
          {meta ? (
            <motion.img
              src={meta.image}
              alt={item.title}
              className="h-full w-full object-contain drop-shadow-[0_18px_22px_rgba(20,30,60,0.18)]"
              loading="lazy"
              animate={{ y: [0, -4, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: index * 0.3 }}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center rounded-2xl bg-[#F7F7F5] text-[#1E5EFF]">
              <Package className="h-6 w-6" />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1 pt-1">
          <h3 className="line-clamp-2 text-[15px] font-semibold text-[#2C2C2C]">{item.title}</h3>
          <p className="mt-1 text-xs text-[#2C2C2C]/55">
            {t("ordc.item.qty", { qty: item.quantity })}
            {item.variantTitle ? ` · ${item.variantTitle}` : ""}
          </p>
          <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
            <Pill label={t("ordc.pill.protocol")} value={meta ? meta.duration(item.quantity) : `${item.quantity}×`} />
            <Pill label={t("ordc.pill.arrives")} value={eta.split(", ")[0]} />
          </div>
        </div>
      </div>
    </motion.article>
  );
}

function Pill({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-[#F7F7F5] px-2.5 py-1.5">
      <p className="text-[9px] font-semibold uppercase tracking-wider text-[#2C2C2C]/45">{label}</p>
      <p className="truncate text-[11px] font-semibold text-[#2C2C2C]">{value}</p>
    </div>
  );
}

function RoutineRow({
  time,
  Icon,
  items,
  last,
  restLabel,
}: {
  time: string;
  Icon: any;
  items: OrderItem[];
  last?: boolean;
  restLabel: string;
}) {
  return (
    <div className={`flex items-start gap-4 p-5 sm:p-6 ${last ? "" : "border-b border-[#D9D9D6]/50"}`}>
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#1E5EFF]/10 text-[#1E5EFF]">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[#2C2C2C]/45">
          {time}
        </p>
        {items.length === 0 ? (
          <p className="mt-1 text-sm text-[#2C2C2C]/40">{restLabel}</p>
        ) : (
          <ul className="mt-1 space-y-1.5">
            {items.map((it, idx) => {
              const meta = getProductMeta(it.handle);
              return (
                <li key={idx} className="flex items-baseline justify-between gap-3">
                  <span className="truncate text-sm font-medium text-[#2C2C2C]">{it.title}</span>
                  <span className="shrink-0 text-[11px] text-[#2C2C2C]/55">
                    {meta?.routine.when}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function ScienceInsightCard({ items, eyebrow }: { items: OrderItem[]; eyebrow: string }) {
  const candidates = items
    .map((i) => getProductMeta(i.handle))
    .filter(Boolean) as NonNullable<ReturnType<typeof getProductMeta>>[];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (candidates.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % candidates.length), 6000);
    return () => clearInterval(t);
  }, [candidates.length]);

  if (candidates.length === 0) return null;
  const insight = candidates[idx].insight;

  return (
    <div className="relative overflow-hidden rounded-[28px] bg-[#2C2C2C] p-6 text-white shadow-[0_18px_50px_rgba(20,30,60,0.18)] sm:p-8">
      <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-[#1E5EFF]/30 blur-3xl" />
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#7AA9FF]">
        {eyebrow}
      </p>
      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.4 }}
          className="relative mt-2"
        >
          <h3 className="font-display text-2xl font-bold leading-tight sm:text-[28px]">
            {insight.headline}
          </h3>
          <p className="mt-3 max-w-xl text-sm text-white/70 sm:text-[15px]">{insight.body}</p>
          <Link
            to={insight.href}
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-[#2C2C2C] transition active:scale-[0.97] hover:bg-white/90"
          >
            {insight.cta} <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function HelpCard({
  icon: Icon,
  title,
  sub,
  to,
  href,
  search,
  onClick,
}: {
  icon: any;
  title: string;
  sub: string;
  to?: string;
  href?: string;
  search?: Record<string, string | null>;
  onClick?: () => void;
}) {
  const inner = (
    <motion.div
      whileHover={{ y: -3 }}
      className="flex h-full flex-col gap-2 rounded-[24px] bg-white p-5 text-left shadow-[0_6px_30px_rgba(20,30,60,0.05)] ring-1 ring-black/[0.04] transition-shadow hover:shadow-[0_14px_40px_rgba(20,30,60,0.08)]"
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#1E5EFF]/10 text-[#1E5EFF]">
        <Icon className="h-4 w-4" />
      </div>
      <p className="mt-1 text-[15px] font-semibold text-[#2C2C2C]">{title}</p>
      <p className="text-xs text-[#2C2C2C]/55">{sub}</p>
    </motion.div>
  );
  if (to) return <Link to={to as any} search={search as any}>{inner}</Link>;
  if (href) return <a href={href} target="_blank" rel="noopener noreferrer">{inner}</a>;
  return (
    <button onClick={onClick} className="text-left">
      {inner}
    </button>
  );
}
